import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/AuthContext";
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { InviteMembersDialog } from "@/components/InviteMembersDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MdOutlineMoreVert } from "react-icons/md";
import { TbAlertTriangle } from "react-icons/tb";
import { Copy, CreditCard, Mail } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';

const OrganizationSettings = () => {
  const { organizationSlug, tab = 'general' } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canManageMembers, setCanManageMembers] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const { axiosInstance } = useAuth();

  const fetchOrganization = async () => {
    try {
      const response = await axiosInstance.get(`/organizations/${organizationSlug}`);
      const orgData = response.data;

      // Add currentUserId and currentUserRole to the organization object
      const currentUser = orgData.members.find(m => m.id === response.data.currentUserId);
      orgData.currentUserId = response.data.currentUserId;
      orgData.currentUserRole = currentUser?.role;

      setOrganization(orgData);

      // Set permissions
      setCanManageMembers(['owner', 'admin'].includes(currentUser?.role));
    } catch (error) {
      console.error('Error fetching organization:', error);
      toast({
        description: "Failed to fetch organization details",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganization();
  }, [organizationSlug]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axiosInstance.put(`/organizations/${organizationSlug}/members/${userId}/role`, {
        role: newRole
      });

      setOrganization(prev => ({
        ...prev,
        members: prev.members.map(member =>
          member.id === userId ? { ...member, role: newRole } : member
        )
      }));

      toast({
        description: "Member role updated successfully",
      });
    } catch (error) {
      console.error('Error updating member role:', error);
      toast({
        title: "Error",
        description: "Failed to update member role",
        variant: "destructive"
      });
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) {
      return;
    }

    try {
      await axiosInstance.delete(`/organizations/${organizationSlug}/members/${userId}`);

      setOrganization(prev => ({
        ...prev,
        members: prev.members.filter(member => member.id !== userId)
      }));

      toast({
        description: "Member removed successfully",
      });
    } catch (error) {
      console.error('Error removing member:', error);
      toast({
        title: "Error",
        description: "Failed to remove member",
        variant: "destructive"
      });
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      const response = await axiosInstance.post(`/organizations/${organizationSlug}/invites/${inviteId}/resend`);
      toast({
        description: "Invite resent successfully",
      });
      setOrganization(prev => ({
        ...prev,
        invites: response.data.invites
      }));
    } catch (error) {
      console.error('Error resending invite:', error);
      toast({
        title: "Error",
        description: "Failed to resend invite",
        variant: "destructive"
      });
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    setIsDeleteLoading(true);
    try {
      const response = await axiosInstance.delete(`/organizations/${organizationSlug}/invites/${inviteId}`);
      toast({
        description: "Invite deleted successfully",
      });
      setOrganization(prev => ({
        ...prev,
        invites: response.data.invites
      }));
    } catch (error) {
      console.error('Error deleting invite:', error);
      toast({
        title: "Error",
        description: "Failed to delete invite",
        variant: "destructive"
      });
    } finally {
      setIsDeleteLoading(false);
      setConfirmDeleteDialogOpen(false);
    }
  };

  const handleTabChange = (value) => {
    if (value === 'general') {
      navigate(`/${organizationSlug}/settings`);
    } else {
      navigate(`/${organizationSlug}/settings/${value}`);
    }
  };

  if (loading) {
    return <Layout>Loading...</Layout>;
  }


  return (
    <Layout>
      <div className="space-y-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/today">Today</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href={`/${organizationSlug}`}>{organization?.name}</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Organization Settings</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Organization Settings</h1>
        </div>

        <Tabs value={tab} onValueChange={handleTabChange} className="space-y-4">
          <TabsList>
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger value="billing">Billing</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>General Settings</CardTitle>
                <CardDescription>
                  Manage your organization's basic information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Organization Name</Label>
                  <Input
                    id="name"
                    value={organization?.name || ''}
                    onChange={(e) => setOrganization(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">Organization URL</Label>
                  <div className="flex">
                    <div className="bg-muted px-3 py-2 border border-r-0 rounded-l-md text-muted-foreground">
                      status.bot/
                    </div>
                    <Input
                      id="slug"
                      value={organization?.slug || ''}
                      className="rounded-l-none"
                      disabled
                    />
                  </div>
                </div>
                <Button>Save Changes</Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="members" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="space-y-1">
                  <CardTitle>Team Members</CardTitle>
                  <CardDescription>
                    Manage your organization's team members and their roles
                  </CardDescription>
                </div>
                {canManageMembers && (
                  <Button onClick={() => setInviteDialogOpen(true)}>
                    Invite Members
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Joined</TableHead>
                      {canManageMembers && (
                        <TableHead>Actions</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {organization?.members?.map((member) => (
                      <TableRow key={member.id}>
                        <TableCell>{member.name}</TableCell>
                        <TableCell>{member.email}</TableCell>
                        <TableCell>
                          {member.id === organization?.currentUserId || !canManageMembers ||
                            (organization?.currentUserRole === 'admin' && member.role === 'owner') ? (
                            <Badge variant="outline" className="capitalize">
                              {member.role}
                            </Badge>
                          ) : (
                            <Select
                              value={member.role}
                              onValueChange={(newRole) => handleRoleChange(member.id, newRole)}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="member">Member</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                                {organization?.currentUserRole === 'owner' && (
                                  <SelectItem value="owner">Owner</SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>
                        <TableCell>{new Date(member.joinedAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          {member.id !== organization?.currentUserId && canManageMembers && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveMember(member.id)}
                            >
                              Remove
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {organization?.invites?.length > 0 && canManageMembers && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <div className="space-y-1">
                  <CardTitle>Pending Invites</CardTitle>
                  <CardDescription>
                    Pending invites are waiting to be accepted by the user.
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Invited By</TableHead>
                      <TableHead className="max-w-[17rem] w-[17rem]">Expires At</TableHead>
                      <TableHead className="max-w-64 w-64">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {organization?.invites?.map((invite) => (
                      <TableRow key={invite.id}>
                        <TableCell>{invite.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {invite.role}
                          </Badge>
                        </TableCell>
                        <TableCell>{invite.invitedBy.name}</TableCell>
                        <TableCell>{new Date(invite.expiresAt).toLocaleDateString()} {new Date(invite.expiresAt) < new Date() && <Badge variant="destructive" className="capitalize ml-2"> Expired</Badge>}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-4">
                          <DropdownMenu >
                            <DropdownMenuTrigger asChild>
                              <Button size="icon" variant="secondary">
                                <MdOutlineMoreVert className="h-5 w-5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" >
                                <DropdownMenuItem onClick={() => {
                                  console.log('invite.copyUrl', invite.copyUrl);
                                  navigator.clipboard.writeText(invite.copyUrl);
                                  toast({
                                    description: "Invite link copied to clipboard",
                                  });
                                }}>
                                  <Copy
                                    className='w-4 h-4 mr-2'
                                  />
                                  Copy invite link
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleResendInvite(invite.id)}>
                                <Mail className="mr-2 h-4 w-4" />
                                Resend Invite
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-600 focus:text-red-600"
                                onClick={() => setConfirmDeleteDialogOpen(invite.id)}
                              >
                                <TbAlertTriangle className="mr-2 h-4 w-4" />
                                Delete Invite
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </CardContent>
              </Card>
            )}

            <InviteMembersDialog
              open={inviteDialogOpen}
              onOpenChange={setInviteDialogOpen}
              organizationSlug={organizationSlug}
              onInvitesSent={() => {
                // Optionally refresh the members list
                fetchOrganization();
              }}
            />

            <ConfirmDeleteDialog
              isOpen={confirmDeleteDialogOpen}
              onClose={() => setConfirmDeleteDialogOpen(false)}
              onDelete={() => handleDeleteInvite(confirmDeleteDialogOpen)}
              deleteLoading={isDeleteLoading}
              header="Delete Invite"
              description="Are you sure you want to delete this invite?"
              showWarning={false}
            />
          </TabsContent>

          <TabsContent value="billing" className="space-y-4">
            <Card className="border-2 border-violet-500/20 bg-violet-500/5">
              <CardContent className="p-8">
                <div className="flex flex-col items-center justify-center space-y-4 py-12">
                  <div className="p-4 bg-violet-500/10 rounded-full">
                    <CreditCard className="w-12 h-12 text-violet-500" />
                  </div>
                  <h2 className="text-2xl font-semibold">Enterprise Billing</h2>
                  <p className="text-center text-muted-foreground max-w-lg text-lg">
                    Billing management is handled through our enterprise support team. Please contact enterprise support to manage your subscription or billing details.
                  </p>
                  <Button
                    size="lg"
                    className="mt-6"
                    onClick={() => window.location.href = 'mailto:enterprise@status.bot'}
                  >
                    Contact Enterprise Support
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default OrganizationSettings;